//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import moment from 'moment-timezone';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import DatePicker from '@components/DatePicker';
import { Alert } from '@components/atoms/alert/Alert';
import { PrimaryAnchor } from '@components/atoms/anchor/Anchor';
import { InvisibleButton } from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import Spinner from '@components/atoms/spinner/Spinner';
import {
  BodyBoldText,
  BodyText,
  LinkPrimaryText,
  SubtitleText,
} from '@components/atoms/text/Text';
import { TrackingId } from '@models/TrackingId';
import { getUserTimeZone } from '@redux/selectors/getUserTimeZone';
import { UIAFooter } from '@uia/layout/UIAFooter';
import {
  MANAGE_RIGHTS_REQUEST_ARTICLE_URL,
  UIA_RIGHTS_FLOW_TRACKING_IDS,
} from '@uia/rights/constants/uiaRightsConstants';
import { getIsRenderedWithinAnotherWorkflow } from '@uia/rights/selectors/getUIARights';
import { isDefined } from '@utils/filterPredicates';

interface UIARightsSetExpiryProps {
  alertText?: string;
  expiryTime?: Date;
  handleOnPreviousClick: () => void;
  handleSubmit: (expiryDate?: Date) => void;
  infoText?: string;
  isExpiryDateOptional: boolean;
  isSubmitInProgress: boolean;
  previousButtonTrackingId: TrackingId;
  submitButtonText: string;
  submitButtonTrackingId: TrackingId;
  title: string;
}

export const UIARightsSetExpiry = ({
  alertText,
  expiryTime,
  handleOnPreviousClick,
  handleSubmit,
  infoText,
  isExpiryDateOptional,
  isSubmitInProgress,
  previousButtonTrackingId,
  submitButtonText,
  submitButtonTrackingId,
  title,
}: UIARightsSetExpiryProps) => {
  // TODO: PD-269777 - Remove once UIA React port is complete
  const isRenderedWithinAnotherWorkflow = useSelector(
    getIsRenderedWithinAnotherWorkflow,
  );
  const timeZone = useSelector(getUserTimeZone);
  // Min date range is exclusive, so subtract 1 day to include todays date
  const minDate = useMemo(
    () => moment().tz(timeZone).subtract(1, 'days').toDate(),
    [timeZone],
  );

  const [expiryDate, setExpiryDate] = useState<Date | undefined>(expiryTime);

  return (
    <OverlayWrapper>
      <FormContainer>
        <SubtitleText>{title}</SubtitleText>
        <Form>
          <BodyText>
            You can{isExpiryDateOptional ? ' optionally' : ''} choose a date
            when you want rights to expire.
            <Link
              href={MANAGE_RIGHTS_REQUEST_ARTICLE_URL}
              target='__blank'
              trackingId={UIA_RIGHTS_FLOW_TRACKING_IDS.SET_EXPIRY_LEARN_MORE}
            >
              Learn More
            </Link>
          </BodyText>
          <DatePickerLabel>
            Expiry date{isExpiryDateOptional ? ' (optional)' : ''}
          </DatePickerLabel>
          <DatePickerControls>
            <DatePickerStyled
              handleDateChanged={(date) => setExpiryDate(date)}
              minDate={minDate}
              timeZone={timeZone}
              trackingId={UIA_RIGHTS_FLOW_TRACKING_IDS.SET_EXPIRY_DATE_PICKER}
              value={expiryDate}
            />
            {isDefined(expiryDate) && (
              <RemoveExpiryDateContainer
                onClick={() => setExpiryDate(undefined)}
                trackingId={
                  UIA_RIGHTS_FLOW_TRACKING_IDS.SET_EXPIRY_REMOVE_EXPIRY_DATE
                }
              >
                <LinkPrimaryText>Remove</LinkPrimaryText>
              </RemoveExpiryDateContainer>
            )}
          </DatePickerControls>
        </Form>
        {isDefined(infoText) && (
          <InfoContainer>
            <StyledIcon name='info' />
            <InfoText>{infoText}</InfoText>
          </InfoContainer>
        )}
        {!!alertText && <StyledAlert type='warning'>{alertText}</StyledAlert>}
        <ApproveContainer>
          <UIAFooter.ActionButton
            isDisabled={isSubmitInProgress}
            onClick={() => handleSubmit(expiryDate)}
            trackingId={submitButtonTrackingId}
          >
            {submitButtonText}
            {isSubmitInProgress && <StyledSpinner />}
          </UIAFooter.ActionButton>
        </ApproveContainer>
      </FormContainer>
      {!isRenderedWithinAnotherWorkflow && (
        <UIAFooter>
          <UIAFooter.TopSection>
            <StyledText>1 of 1</StyledText>
            <VerticalDivider />
            <UIAFooter.PreviousButton
              onClick={() => handleOnPreviousClick()}
              trackingId={previousButtonTrackingId}
            />
          </UIAFooter.TopSection>
          <UIAFooter.BottomSection>
            <UIAFooter.ActionButton
              isDisabled={true}
              trackingId={
                UIA_RIGHTS_FLOW_TRACKING_IDS.SET_EXPIRY_FOOTER_DONE_BUTTON
              }
            >
              Done
            </UIAFooter.ActionButton>
          </UIAFooter.BottomSection>
        </UIAFooter>
      )}
    </OverlayWrapper>
  );
};

const Link = styled(PrimaryAnchor)(
  ({ theme }) => `
    margin-left: ${theme.spacings[1]};
  `,
);

const DatePickerStyled = styled(DatePicker)(
  ({ theme }) => `
    max-width: ${theme.smallMeasure};
  `,
);

const OverlayWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  width: 100%;
`;

const FormContainer = styled.div(
  ({ theme }) => `
    padding: ${theme.spacings[3]};
  `,
);

const Form = styled.div(
  ({ theme }) => `
    padding: ${theme.spacings[3]};
    margin-top: ${theme.spacings[3]};
    background: ${theme.colors.gray0};
  `,
);

const DatePickerControls = styled.div`
  display: flex;
  justify-content: space-between;
`;

const VerticalDivider = styled.div(
  ({ theme }) => `
    height: ${theme.spacings[6]};
    border-left: ${theme.borderWidths[1]} solid ${theme.colors.gray1};
  `,
);

const StyledAlert = styled(Alert)(
  ({ theme }) => `
  margin: ${theme.spacings[3]} 0;
`,
);

const InfoContainer = styled(BodyText)(
  ({ theme }) => `
  display: flex;
  align-items: center;
  margin-top: ${theme.spacings[3]};
`,
);

const InfoText = styled.span(
  ({ theme }) => `
    margin-left: ${theme.spacings[1]};
  `,
);

const StyledText = styled(BodyText)(
  ({ theme }) => `
  margin: 0 ${theme.spacings[3]};
  color: ${theme.colors.teal1};
`,
);

const StyledSpinner = styled(Spinner)(
  ({ theme }) => `
  width: ${theme.spacings[3]};
  height: ${theme.spacings[3]};
`,
);

const DatePickerLabel = styled(BodyBoldText)(
  ({ theme }) => `
  margin-top: ${theme.spacings[3]};
`,
);

const RemoveExpiryDateContainer = styled(InvisibleButton)``;

const ApproveContainer = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: center;
    margin-top: ${theme.spacings[5]};
  `,
);

const StyledIcon = styled(Icon)(
  ({ theme }) => `
    color: ${theme.colors.aliases.bodySecondary};
  `,
);
